import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useProduct, useRelatedProducts } from "../hooks/useProducts";
import { useSGPhotosByProduct } from "../hooks/useSGGallery";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";
import OptimizedImage from "../components/OptimizedImage";
import ImageZoom from "../components/ImageZoom";
import FadeIn from "../components/FadeIn";
import useSEO from "../hooks/useSEO";
import { imagePresets } from "../utils/imageOptimizer";

const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
    <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BagIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 6H21" stroke="currentColor" strokeWidth="2" />
    <path d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Función para obtener todas las imágenes de un producto desde Supabase
function getProductImages(product) {
  // Si el producto tiene imágenes de Supabase Storage, usarlas
  if (product && product.images && product.images.length > 0) {
    return product.images;
  }
  
  // Fallback: si no hay imágenes en Supabase, retornar array vacío
  return [];
}

export default function ProductDetail() {
  const { categoryId, productCode } = useParams();
  const navigate = useNavigate();
  const { product, loading, error } = useProduct(categoryId, productCode);
  const { relatedProducts } = useRelatedProducts(categoryId, productCode, 4);
  const { photos: sgPhotos } = useSGPhotosByProduct(productCode);
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showAddedNotification, setShowAddedNotification] = useState(false);

  useEffect(() => {
    if (!loading && !product) {
      navigate("/shop");
    }
  }, [product, loading, navigate]);

  // Obtener imágenes del producto desde Supabase (antes de los returns)
  const productImages = useMemo(() => {
    return product ? getProductImages(product) : [];
  }, [product]);
  const mainImage = productImages[selectedImage] || productImages[0] || '';

  useEffect(() => {
    if (!productImages || productImages.length === 0) return;

    const nextImage = productImages[selectedImage + 1];
    if (nextImage) {
      const img = new Image();
      img.src = imagePresets.card(nextImage);
      img.fetchPriority = 'low';
    }
  }, [productImages, selectedImage]);

  const handleThumbnailClick = useCallback((index) => {
    setSelectedImage(index);
  }, []);

  // ── useSEO SIEMPRE antes de cualquier return condicional (Reglas de Hooks) ──
  const extendedDescription = product?.description ||
    "This piece is probably one of the most iconic sculptures of all times. Sign of beauty and perfection, this sculpture was made by an unknown artist around 130-100 a.e.c. in Greece. After centuries of disappearance, it was found by a french archeologist, who later sold the sculpture to the french monarchy under the power of Luis XIV. This sculpture has been one of the most used casts in classic academies and it's still an essential figure for nowadays art academies.";

  const priceValue = product?.price
    ? product.price.replace(/[^0-9,]/g, '').replace(',', '.')
    : '';

  useSEO(product ? {
    title: `${product.name} — ${product.artist}`,
    description: `${product.name} de ${product.artist}. ${product.dimensions ? product.dimensions + '. ' : ''}Reproducción artesanal en escayola en Barcelona. ${product.price}.`,
    canonical: `/product/${categoryId}/${productCode}`,
    ogImage: productImages[0] || undefined,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          name: product.name,
          description: extendedDescription,
          image: productImages,
          brand: { '@type': 'Brand', name: 'The Spanish Gipsoteca' },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'EUR',
            price: priceValue,
            availability: 'https://schema.org/InStock',
            url: `https://thespanishgipsoteca.com/product/${categoryId}/${productCode}`,
            seller: { '@type': 'Organization', name: 'The Spanish Gipsoteca' },
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Inicio',
              item: 'https://thespanishgipsoteca.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Tienda',
              item: 'https://thespanishgipsoteca.com/shop',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: product.name,
              item: `https://thespanishgipsoteca.com/product/${categoryId}/${productCode}`,
            },
          ],
        },
      ],
    },
  } : {});

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-20 flex items-center justify-center">
        <p className="text-white/70">Cargando producto...</p>
      </div>
    );
  }

  if (!product || error) {
    return null;
  }

  const allImages = productImages;

  return (
    <div className="flex min-h-screen flex-col bg-black text-white pt-16 sm:pt-20">
      {/* Header simplificado para la página de producto */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between bg-black/50 px-4 py-3 backdrop-blur-md sm:px-6 sm:py-4 md:px-10">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-white transition-colors hover:text-accent sm:text-base flex-shrink-0"
        >
          ← Volver
        </button>
        <h1 className="font-display text-xs uppercase tracking-[0.2em] text-white sm:text-sm sm:tracking-[0.3em] md:text-base md:tracking-[0.35em] lg:text-lg flex-shrink min-w-0 px-2 text-center">
          <span className="hidden sm:inline">THE SPANISH GIPSOTECA</span>
          <span className="sm:hidden">TSG</span>
        </h1>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button className="text-white transition-colors hover:text-accent">
            <SearchIcon />
          </button>
          <button className="text-white transition-colors hover:text-accent">
            <BagIcon />
          </button>
        </div>
      </div>

      <div className="flex-1">
      {/* Layout Principal - Imagen grande + thumbnails pequeñas al lado */}
      <section className="bg-black py-12 sm:py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12">
            {/* Columna izquierda - Imágenes */}
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
              {/* Thumbnails pequeñas - Vertical en desktop, horizontal en móvil */}
              {allImages.length > 1 && (
                <div className="flex lg:flex-col gap-2 lg:gap-3 order-2 lg:order-1">
                  {allImages.map((img, index) => (
                    <div
                      key={index}
                      className={`flex-shrink-0 aspect-[3/4] w-16 h-20 lg:w-20 lg:h-28 cursor-pointer overflow-hidden bg-black border-2 transition-all ${
                        selectedImage === index
                          ? 'border-white scale-105'
                          : 'border-white/20 hover:border-white/50'
                      }`}
                      onClick={() => handleThumbnailClick(index)}
                    >
                      <OptimizedImage
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        className="h-full w-full object-cover"
                        priority={false}
                        aspectRatio="3/4"
                        size="galleryThumb"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Imagen principal grande con zoom */}
              <div className="flex-1 order-1 lg:order-2">
                <div className="aspect-[3/4] bg-black">
                  <ImageZoom
                    src={mainImage}
                    alt={product.name}
                    className="h-full w-full"
                    zoomScale={2.5}
                    zoomSize={350}
                  />
                </div>
              </div>
            </div>

            {/* Columna derecha - Información del producto */}
            <FadeIn className="lg:col-span-1" direction="left" delay={100}>
                <h2 className="mb-2 font-display text-xl uppercase tracking-[0.1em] text-white sm:text-2xl sm:tracking-[0.15em] md:text-3xl lg:text-4xl">
                  {product.name.toUpperCase()}
                </h2>
                <p className="mb-3 text-xs uppercase tracking-[0.15em] text-white/70 sm:mb-4 sm:text-sm sm:tracking-[0.2em]">
                  {product.artist}
                </p>
                <p className="mb-4 text-base text-white/90 sm:mb-6 sm:text-lg">
                  {product.price}
                  <span className="text-xs text-white/50 ml-1">(+ gastos de envío)</span>
                </p>
                <p className="mb-4 text-xs leading-relaxed text-white/80 sm:mb-6 sm:text-sm">
                  {extendedDescription}
                </p>
                <div className="mb-4 space-y-1.5 text-xs text-white/70 sm:mb-6 sm:space-y-2 sm:text-sm">
                  <p>{product.dimensions}</p>
                  <p>Code: {product.code}</p>
                </div>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-center border border-white/20 w-full sm:w-auto">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-white transition hover:bg-white/10 sm:px-3"
                    >
                      −
                    </button>
                    <span className="flex-1 px-4 py-2 text-center text-white sm:flex-none">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-white transition hover:bg-white/10 sm:px-3"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (product) {
                        setIsAddingToCart(true);
                        setTimeout(() => setIsAddingToCart(false), 600);
                        setShowAddedNotification(true);
                        setTimeout(() => setShowAddedNotification(false), 3000);
                        addToCart(product, quantity);
                      }
                    }}
                    className={`w-full rounded-sm bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.1em] text-black transition-all hover:bg-white/90 sm:w-auto sm:flex-1 sm:px-6 sm:py-2 sm:text-sm sm:tracking-[0.15em] ${
                      isAddingToCart ? 'animate-pulse scale-95' : ''
                    }`}
                  >
                    {isAddingToCart ? 'Añadiendo...' : 'Añadir al carrito'}
                  </button>
                </div>
            </FadeIn>
          </div>
        </div>
        </section>

      </div>

      {/* SG Gallery - Fotos en la vida real - Solo mostrar si hay fotos relacionadas */}
      {sgPhotos && sgPhotos.length > 0 && (
        <section className="border-t border-white/10 bg-black py-12 sm:py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10">
            <FadeIn className="mb-8 sm:mb-10 flex items-center justify-between">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.3em] text-accent sm:text-sm">SG Gallery</p>
                <h3 className="font-display text-lg uppercase tracking-[0.15em] text-white sm:text-xl sm:tracking-[0.2em] md:text-2xl">
                  En la Vida Real
                </h3>
                <p className="mt-2 text-xs text-white/60 sm:text-sm">
                  Descubre cómo se ve este producto en espacios reales
                </p>
              </div>
              <Link
                to="/sg-gallery"
                className="text-xs uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-white sm:text-sm"
              >
                Ver galería →
              </Link>
            </FadeIn>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {sgPhotos.map((photo, index) => (
                <Link
                  key={photo.id}
                  to={`/sg-gallery/${photo.sg_gallery_collections?.slug || ''}`}
                  className="group relative block aspect-[4/3] overflow-hidden bg-black/50"
                >
                  <OptimizedImage
                    src={photo.image_url || ''}
                    alt="Foto de SG Gallery"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    priority={index < 3}
                    aspectRatio="4/3"
                    size="card"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {photo.sg_gallery_collections && (
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-xs uppercase tracking-[0.15em] text-white/60 sm:text-sm">
                        {photo.sg_gallery_collections.name}
                      </p>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Sección de Productos Relacionados */}
      {relatedProducts.length > 0 && (
        <section className="border-t border-white/10 bg-black py-12 sm:py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10">
            <div className="mb-8 sm:mb-10 flex items-center justify-between">
              <h3 className="font-display text-lg uppercase tracking-[0.15em] text-white sm:text-xl sm:tracking-[0.2em] md:text-2xl">
                También te puede interesar
              </h3>
              <Link 
                to="/shop" 
                className="text-xs uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-white sm:text-sm"
              >
                Ver todo →
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4 lg:gap-8">
              {relatedProducts.map((relatedProduct, index) => (
                <Link
                  key={relatedProduct.code}
                  to={`/product/${relatedProduct.categoryId}/${relatedProduct.code}`}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-black/50 mb-3 sm:mb-4">
                    <OptimizedImage
                      src={relatedProduct.images[0] || ''}
                      alt={relatedProduct.name}
                      className="transition-transform duration-500 group-hover:scale-105"
                      priority={index < 2}
                      aspectRatio="3/4"
                      size="card"
                      objectFit="contain"
                    />
                    <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20" />
                  </div>
                  <h4 className="text-xs uppercase tracking-[0.1em] text-white/80 transition-colors group-hover:text-white sm:text-sm sm:tracking-[0.15em]">
                    {relatedProduct.name}
                  </h4>
                  <p className="mt-1 text-xs text-white/50 sm:text-sm">
                    {relatedProduct.price}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Notificación de producto añadido */}
      {showAddedNotification && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform animate-in fade-in slide-in-from-bottom-4">
          <div className="rounded-sm border border-white/20 bg-black/95 px-6 py-4 shadow-xl backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-white">
                Producto añadido al carrito
              </p>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

